import * as adminActions from "../../../store/actions/temp/admin/adminActions";
import TextField from "../../common/TextField";
import {
  appendCreateOption,
  configKeyFromSplat,
  isValidConfigKey,
  mergeConfigKeyOptions,
  optionKey,
  staticDataPath,
} from "./StaticDataEditor.support";
import {
  EditRounded as EditIcon,
  ExposureRounded as DiffIcon,
  HistoryRounded as HistoryIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Alert,
  createFilterOptions,
  Divider,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import * as strings from "common/strings";
import Component from "components/Component";
import CodeEditor, {
  DiffEditor,
  EditorPreferenceToggle,
} from "components/admin/CodeEditor";
import ConfigHistoryModal from "components/admin/userConfig/ConfigHistoryModal";
import NewConfigDialog from "components/admin/userConfig/NewConfigDialog";
import StageConfigDialog from "components/admin/userConfig/StageConfigDialog";
import Button from "components/common/Button";
import CopyButton from "components/common/CopyButton";
import DefaultLoader from "components/common/DefaultLoader";
import GUIDGenerator from "components/common/GUIDGenerator";
import IconButton from "components/common/IconButton";
import * as config from "config";
import { detailedDiff } from "deep-object-diff";
import { useSnackbar } from "notistack";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  useListStaticDataQuery,
  useLazyGetStaticDataQuery,
  useUpdateStaticDataMutation,
  useGetDefaultStaticDataQuery,
  useGetOverlayStaticDataQuery,
} from "services/adminService";
import { useListConfigQuery } from "services/staticDataService";
import * as adminSelectors from "store/selectors/temp/admin/adminSelectors";
import * as utils from "utils";

const configKeyFilter = createFilterOptions();

const diff = (oldJson, candidateJsonText) => {
  try {
    return {
      hasError: false,
      diff: detailedDiff(oldJson, JSON.parse(candidateJsonText)),
    };
  } catch (e) {
    return {
      hasError: true,
      message: `${e.name} ${e.message}`,
    };
  }
};

const DefaultOverlayFileDialog = ({
  open,
  close,
  dataToDisplay,
  copyToEditorAndClose,
}) => {
  return (
    <Dialog
      open={open}
      onClose={() => close()}
      maxWidth={false}
      PaperProps={{
        style: {
          width: "90vw",
          height: "90vh",
          maxWidth: "90vw",
          maxHeight: "90vh",
          borderRadius: 12,
        },
      }}
    >
      <DialogContent>
        <div style={{ height: "calc(90vh - 120px)" }}>
          <CodeEditor
            value={JSON.stringify(dataToDisplay, null, 2)}
            language={"json"}
            readOnly
            height={"calc(90vh - 120px)"}
          />
        </div>
      </DialogContent>
      <DialogActions style={{ justifyContent: "center", paddingBottom: 32 }}>
        <Button
          variant={"contained"}
          color={"secondary"}
          onClick={() => close()}
        >
          {"Close"}
        </Button>
        {copyToEditorAndClose && (
          <Button
            variant={"contained"}
            color={"secondary"}
            onClick={() => copyToEditorAndClose()}
          >
            {"Copy To Editor"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const StaticDiff = ({
  data,
  newData,
  companyId,
  staticDataKey,
  postSaveCallback,
  saveUserLogMessage,
  userLogMessage,
  hasUserLogMessage,
}) => {
  const { hasError, diff: diffData, message: errorMessage } = diff(
    data,
    newData
  );
  const noChanges =
    Object.keys({
      ...diffData?.added,
      ...diffData?.deleted,
      ...diffData?.updated,
    }).length === 0;
  const [
    updateStaticData,
    { isLoading: submitLoading, isError: submitError },
  ] = useUpdateStaticDataMutation();
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Grid container spacing={2}>
      {hasError ? (
        <Grid item xs={12}>
          <Alert severity={"error"}>{errorMessage ?? "Unknown Error"}</Alert>
        </Grid>
      ) : noChanges ? (
        <Grid item xs={12}>
          <Alert severity={"warning"}>{"No changes detected"}</Alert>
        </Grid>
      ) : (
        <>
          <Grid item xs={12}>
            <DiffEditor
              original={JSON.stringify(data, null, 2)}
              modified={newData}
              language={"json"}
            />
          </Grid>
          <Grid item xs={12}>
            <Alert severity={"success"}>{"This is valid JSON."}</Alert>
          </Grid>
          <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
            <Typography
              variant={"h4"}
              align={"center"}
              sx={{ fontWeight: "bold" }}
            >
              {companyId}
            </Typography>
            <Divider sx={{ mt: 2 }} />
          </Grid>
          <Grid container item xs={12} alignItems={"center"}>
            <Grid item xs={8}>
              <TextField
                variant={"filled"}
                label={"Reason for change"}
                fullWidth
                onChange={(e) => saveUserLogMessage(e.target.value)}
                value={userLogMessage}
              />
            </Grid>
            <Grid
              item
              container
              xs={4}
              justifyContent={"right"}
              spacing={1}
              wrap={"nowrap"}
            >
              <Grid item>
                <Button
                  variant={"contained"}
                  disableElevation
                  color={"secondary"}
                  hasError={hasError}
                  isDisabled={hasError || submitLoading}
                  onClick={() => setStageDialogOpen(true)}
                >
                  {"Stage"}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant={"contained"}
                  disableElevation
                  color={"primary"}
                  hasError={hasError || submitError}
                  isDisabled={hasError || submitLoading || !hasUserLogMessage}
                  isLoading={submitLoading}
                  onClick={() => {
                    updateStaticData({
                      companyId,
                      key: staticDataKey,
                      data: JSON.parse(newData),
                      userLogMessage,
                    }).then(() => {
                      if (postSaveCallback) {
                        postSaveCallback();
                      }
                    });
                  }}
                >
                  {"Save Changes"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <StageConfigDialog
            open={stageDialogOpen}
            onClose={(staged) => {
              setStageDialogOpen(false);
              if (staged) {
                enqueueSnackbar("Config change staged for release", {
                  variant: "success",
                });
              }
            }}
            companyId={companyId}
            staticDataKey={staticDataKey}
            getData={() => JSON.parse(newData)}
            initialReason={userLogMessage}
          />
        </>
      )}
    </Grid>
  );
};

const StaticDataEditor = ({
  saveUserLogMessage,
  userLogMessage,
  hasUserLogMessage,
}) => {
  const params = useParams();
  const companyId = params.companyId;
  const splat = params["*"] ?? "";
  const urlKey = configKeyFromSplat(splat);
  const navigate = useNavigate();

  const allStaticDataKeys = useListConfigQuery();
  const existingStaticDataKeys = useListStaticDataQuery(
    { companyId },
    { skip: companyId == null }
  );
  const [lastStaticDataKey, setLastStaticDataKey] = useState(null);
  const [staticDataKey, setStaticDataKey] = useState(null);
  const [newStaticData, setNewStaticData] = useState(null);
  const [differenceToggle, setDifferenceToggle] = useState(false);
  const [isNewStaticData, setIsNewStaticData] = useState(false);
  const [staticDataTrigger, staticData] = useLazyGetStaticDataQuery();
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);
  const [overlayDialogOpen, setOverlayDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const baselineRef = useRef(null);
  const [isStale, setIsStale] = useState(false);
  const [newConfigInput, setNewConfigInput] = useState(null);

  const configKeyOptions = useMemo(
    () =>
      mergeConfigKeyOptions(
        allStaticDataKeys.data,
        existingStaticDataKeys.data
      ),
    [allStaticDataKeys.data, existingStaticDataKeys.data]
  );

  const selectConfigKey = useCallback(
    (key) => {
      setStaticDataKey(key);
      baselineRef.current = null;
      setIsStale(false);
      if (existingStaticDataKeys.data?.includes(key)) {
        staticDataTrigger({ companyId, key });
        setIsNewStaticData(false);
      } else {
        setIsNewStaticData(true);
      }
      setNewStaticData(null);
    },
    [companyId, existingStaticDataKeys.data, staticDataTrigger]
  );
  const historyTitle = useMemo(() => {
    if (!staticDataKey) return "Untitled Config";
    if (!allStaticDataKeys.data) return strings.toSentenceCase(staticDataKey);
    const entry = allStaticDataKeys.data.find((e) => e.key === staticDataKey);
    return entry?.displayName ?? strings.toSentenceCase(staticDataKey);
  }, [staticDataKey, allStaticDataKeys.data]);

  const [expectDefaultStaticData, expectOverlayStaticData] = useMemo(() => {
    if (
      staticDataKey == null ||
      allStaticDataKeys.status !== "fulfilled" ||
      !allStaticDataKeys.data
    )
      return [false, false];
    const entry = allStaticDataKeys.data.find((e) => e.key === staticDataKey);
    return [entry?.hasDefault ?? false, entry?.hasOverlay ?? false];
  }, [staticDataKey, allStaticDataKeys.data, allStaticDataKeys?.status]);

  useEffect(() => {
    if (lastStaticDataKey !== staticDataKey) {
      setLastStaticDataKey(staticDataKey);
      setDifferenceToggle(false);
    }
  }, [lastStaticDataKey, staticDataKey]);

  const {
    data: defaultStaticDataRaw,
    status: defaultStaticDataStatus,
  } = useGetDefaultStaticDataQuery(
    { key: staticDataKey },
    { skip: !expectDefaultStaticData }
  );

  const {
    data: overlayStaticDataRaw,
    status: overlayStaticDataStatus,
  } = useGetOverlayStaticDataQuery(
    { key: staticDataKey },
    { skip: !expectOverlayStaticData }
  );
  const defaultStaticData = useMemo(
    () =>
      expectDefaultStaticData && defaultStaticDataStatus === "fulfilled"
        ? defaultStaticDataRaw
        : null,
    [expectDefaultStaticData, defaultStaticDataRaw, defaultStaticDataStatus]
  );
  const overlayStaticData = useMemo(
    () =>
      expectOverlayStaticData && overlayStaticDataStatus === "fulfilled"
        ? overlayStaticDataRaw
        : null,
    [expectOverlayStaticData, overlayStaticDataStatus, overlayStaticDataRaw]
  );

  const loadedData = isNewStaticData ? {} : staticData?.data;
  useEffect(() => {
    if (companyId) {
      setStaticDataKey(null);
      setNewStaticData(null);
      baselineRef.current = null;
      setIsStale(false);
    }
  }, [companyId]);

  // Apply the URL's config-key segment to the editor selection
  useEffect(() => {
    if (!companyId) return;
    if (splat !== "" && urlKey === null) {
      navigate(staticDataPath(companyId, null), { replace: true });
      return;
    }
    if (urlKey !== null && urlKey !== splat) {
      navigate(staticDataPath(companyId, urlKey), { replace: true });
      return;
    }
    if (existingStaticDataKeys.data == null) return;
    if (urlKey !== null) {
      const lowerUrlKey = urlKey.toLowerCase();
      const canonicalKey =
        existingStaticDataKeys.data.find(
          (key) => key.toLowerCase() === lowerUrlKey
        ) ??
        configKeyOptions.find(
          (option) => option.key.toLowerCase() === lowerUrlKey
        )?.key;
      if (canonicalKey != null && canonicalKey !== urlKey) {
        navigate(staticDataPath(companyId, canonicalKey), { replace: true });
        return;
      }
    }
    if (urlKey !== staticDataKey) {
      selectConfigKey(urlKey);
    }
  }, [
    companyId,
    splat,
    urlKey,
    staticDataKey,
    selectConfigKey,
    existingStaticDataKeys.data,
    configKeyOptions,
    navigate,
  ]);

  // Snapshot baseline when data first loads
  useEffect(() => {
    if (staticData?.data && !isNewStaticData && baselineRef.current === null) {
      baselineRef.current = JSON.stringify(staticData.data);
    }
  }, [staticData?.data, isNewStaticData]);

  // Poll for external changes without updating editor data
  useEffect(() => {
    if (!staticDataKey || !companyId || isNewStaticData || isStale) return;
    const interval = setInterval(async () => {
      try {
        const response = await utils.authenticatedFetch(
          config.endpoints.admin(`static-data/${companyId}/${staticDataKey}`),
          {}
        );
        const data = await response.json();
        if (
          baselineRef.current !== null &&
          JSON.stringify(data) !== baselineRef.current
        ) {
          setIsStale(true);
        }
      } catch {
        // Silently ignore poll failures
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [staticDataKey, companyId, isNewStaticData, isStale]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!staticDataKey) return;

      if (event.metaKey && event.key === "d") {
        event.preventDefault();
        if (!differenceToggle) setDifferenceToggle(true);
      } else if (event.metaKey && event.key === "e") {
        event.preventDefault();
        if (differenceToggle) setDifferenceToggle(false);
      }
    },
    [differenceToggle, staticDataKey, setDifferenceToggle]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const jsonValue =
    newStaticData ??
    (isNewStaticData ? "" : JSON.stringify(staticData?.data, null, 2));

  return (
    <>
      <GUIDGenerator />
      <NewConfigDialog
        open={newConfigInput !== null}
        initialInput={newConfigInput ?? ""}
        existingKeys={configKeyOptions.map((option) => option.key)}
        onClose={() => setNewConfigInput(null)}
        onCreate={(key) => {
          setNewConfigInput(null);
          navigate(staticDataPath(companyId, key));
        }}
      />
      <ConfigHistoryModal
        open={historyOpen}
        close={() => setHistoryOpen(false)}
        companyId={companyId}
        staticDataKey={staticDataKey}
        title={historyTitle}
      />
      <Component
        title={"Configuration Files"}
        loading={
          allStaticDataKeys.isFetching || existingStaticDataKeys.isFetching
        }
      >
        {companyId && (
          <Grid container spacing={2}>
            {allStaticDataKeys.data && (
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={configKeyOptions}
                  filterOptions={(options, params) =>
                    appendCreateOption(
                      configKeyFilter(options, params),
                      params.inputValue,
                      options
                    )
                  }
                  getOptionKey={(option) =>
                    typeof option === "string" ? option : option.key
                  }
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option.displayName ?? strings.toSentenceCase(option.key)
                  }
                  value={
                    configKeyOptions.filter(
                      (x) => x.key === staticDataKey
                    )?.[0] ??
                    staticDataKey ??
                    null
                  }
                  onChange={(_, value) => {
                    const key = optionKey(value);
                    if (key === null) {
                      navigate(staticDataPath(companyId, null));
                      return;
                    }
                    const isKnownKey = configKeyOptions.some(
                      (option) => option.key === key
                    );
                    if (value?.isNewKey || !isKnownKey) {
                      setNewConfigInput(
                        typeof value === "string" ? value : key
                      );
                      return;
                    }
                    if (!isValidConfigKey(key)) return;
                    navigate(staticDataPath(companyId, key));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant={"outlined"}
                      label={staticDataKey == null ? "Select Config" : null}
                      helperText={
                        isNewStaticData && staticDataKey != null
                          ? "New config — it does not exist yet and will be created on save or when a staged change is applied"
                          : null
                      }
                    />
                  )}
                />
              </Grid>
            )}
            {staticData.isFetching ? (
              <Grid item xs={12}>
                <DefaultLoader
                  size={60}
                  style={{
                    display: "block",
                    textAlign: "center",
                  }}
                />
              </Grid>
            ) : (
              (isNewStaticData || staticData.data) &&
              staticDataKey && (
                <>
                  {isStale && (
                    <Grid item xs={12}>
                      <Alert
                        severity={"warning"}
                        action={
                          <Button
                            color={"inherit"}
                            size={"small"}
                            onClick={() => window.location.reload()}
                          >
                            {"Refresh"}
                          </Button>
                        }
                      >
                        {
                          "This config has been updated by another user. Refresh to see the latest version."
                        }
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <ToggleButtonGroup
                        value={differenceToggle ? "Diff" : "Edit"}
                        onChange={() => setDifferenceToggle(!differenceToggle)}
                        aria-label={
                          "Toggle between edit and diff modes (⌘+E / ⌘+D)"
                        }
                        color={"primary"}
                        size={"small"}
                        exclusive
                      >
                        <ToggleButton value={"Edit"} title={"Edit mode (⌘+E)"}>
                          <EditIcon
                            fontSize={"small"}
                            sx={{ marginRight: "3px" }}
                          />
                          <span style={{ paddingRight: "3px" }}>{"Edit"}</span>
                        </ToggleButton>
                        <ToggleButton value={"Diff"} title={"Diff mode (⌘+D)"}>
                          <DiffIcon
                            fontSize={"small"}
                            sx={{ marginRight: "3px" }}
                          />
                          <span style={{ paddingRight: "2px" }}>{"Diff"}</span>
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {!differenceToggle &&
                        newStaticData != null &&
                        newStaticData !==
                          JSON.stringify(staticData?.data, null, 2) && (
                          <Button
                            variant={"contained"}
                            disableElevation
                            color={"primary"}
                            onClick={() =>
                              setNewStaticData(
                                JSON.stringify(staticData?.data, null, 2)
                              )
                            }
                          >
                            {"Undo edits"}
                          </Button>
                        )}
                      {defaultStaticData && !differenceToggle && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Typography variant={"body"}>
                            {"There is a default"}
                          </Typography>
                          <Button
                            variant={"contained"}
                            disableElevation
                            color={"primary"}
                            onClick={() => setDefaultDialogOpen(true)}
                          >
                            {"Show default"}
                          </Button>
                          <DefaultOverlayFileDialog
                            open={defaultDialogOpen}
                            close={() => setDefaultDialogOpen(false)}
                            dataToDisplay={defaultStaticData}
                            copyToEditorAndClose={() => {
                              setNewStaticData(
                                JSON.stringify(defaultStaticData, null, 2)
                              );
                              setDefaultDialogOpen(false);
                            }}
                          />
                        </div>
                      )}
                      {overlayStaticData && !differenceToggle && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Typography variant={"body"}>
                            {"There is an override"}
                          </Typography>
                          <Button
                            variant={"contained"}
                            disableElevation
                            color={"primary"}
                            onClick={() => setOverlayDialogOpen(true)}
                          >
                            {"Show overlay"}
                          </Button>
                          <DefaultOverlayFileDialog
                            open={overlayDialogOpen}
                            close={() => setOverlayDialogOpen(false)}
                            dataToDisplay={overlayStaticData}
                            copyToEditorAndClose={null}
                          />
                        </div>
                      )}
                      <Stack direction={"row"} spacing={1}>
                        <IconButton
                          icon={HistoryIcon}
                          tooltip={"View history"}
                          isDisabled={isNewStaticData}
                          onClick={() => setHistoryOpen(true)}
                        />
                        <CopyButton content={jsonValue} />
                        <EditorPreferenceToggle />
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    {differenceToggle ? (
                      <StaticDiff
                        data={loadedData}
                        newData={
                          newStaticData ??
                          (isNewStaticData
                            ? ""
                            : JSON.stringify(staticData?.data, null, 2))
                        }
                        companyId={companyId}
                        staticDataKey={staticDataKey}
                        postSaveCallback={async () => {
                          setNewStaticData(null);
                          setIsNewStaticData(false);
                          baselineRef.current = null;
                          setIsStale(false);
                          await staticDataTrigger({
                            companyId,
                            key: staticDataKey,
                          });
                          existingStaticDataKeys?.refetch();
                          setDifferenceToggle(false);
                        }}
                        saveUserLogMessage={saveUserLogMessage}
                        userLogMessage={userLogMessage}
                        hasUserLogMessage={hasUserLogMessage}
                      />
                    ) : (
                      <CodeEditor
                        value={jsonValue}
                        language={"json"}
                        onChange={(value) => setNewStaticData(value)}
                      />
                    )}
                  </Grid>
                </>
              )
            )}
          </Grid>
        )}
      </Component>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    userLogMessage: adminSelectors.userLogMessage(state),
    hasUserLogMessage: adminSelectors.hasUserLogMessage(state),
  };
};

const mapDispatchToProps = {
  saveUserLogMessage: adminActions.userLogMessage.update,
};

export default connect(mapStateToProps, mapDispatchToProps)(StaticDataEditor);
